import { Flame } from 'lucide-react';

interface Props {
  current: number;
  longest: number;
  lastActivityDate: string | null;
}

function getDayLabels(): string[] {
  return ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
}

function getWeekActivity(current: number, lastActivityDate: string | null): boolean[] {
  const today = new Date();
  const todayDow = (today.getUTCDay() + 6) % 7; // 0=Mon, 6=Sun
  const lastDate = lastActivityDate ? new Date(lastActivityDate).toISOString().split('T')[0] : null;
  const todayStr = today.toISOString().split('T')[0];
  const isActiveToday = lastDate === todayStr;

  // Build array of 7 booleans for Mon-Sun of this week
  const week: boolean[] = Array(7).fill(false);

  if (current === 0) return week;

  // Fill backwards from today (or yesterday if not active today)
  const activeDays = isActiveToday ? current : Math.min(current, todayDow);
  const startDow = isActiveToday ? todayDow : todayDow - 1;

  for (let i = 0; i < activeDays && (startDow - i) >= 0; i++) {
    week[startDow - i] = true;
  }

  return week;
}

export default function StreakWidget({ current, longest, lastActivityDate }: Props) {
  const days = getDayLabels();
  const week = getWeekActivity(current, lastActivityDate);
  const todayDow = (new Date().getUTCDay() + 6) % 7;
  const todayStr = new Date().toISOString().split('T')[0];
  const lastDate = lastActivityDate ? new Date(lastActivityDate).toISOString().split('T')[0] : null;
  const isActiveToday = lastDate === todayStr;

  // Next milestone
  let nextMilestone = null;
  for (const m of [7, 30, 100]) {
    if (current < m) {
      nextMilestone = { days: m, remaining: m - current };
      break;
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 text-white">
        <div className="flex items-center gap-2">
          <Flame className="w-6 h-6" />
          <div>
            <h3 className="text-lg font-bold font-display">
              {current} Day Streak
            </h3>
            <p className="text-xs text-amber-100">Best: {longest} days</p>
          </div>
        </div>
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Weekly calendar */}
        <div className="flex justify-between">
          {days.map((label, i) => {
            const active = week[i];
            const isToday = i === todayDow;
            return (
              <div key={i} className="flex flex-col items-center gap-1">
                <span className="text-[10px] font-medium text-[#6B7280]">{label}</span>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    active
                      ? 'bg-amber-500 text-white'
                      : isToday
                      ? 'border-2 border-amber-400 text-amber-500'
                      : 'bg-[#F8F9FA] text-[#6B7280]'
                  }`}
                >
                  {active ? <Flame className="w-3.5 h-3.5" /> : ''}
                </div>
              </div>
            );
          })}
        </div>

        {/* Day-one nudge — celebrate the first day and prime the return. */}
        {current === 1 && isActiveToday && (
          <p className="text-xs text-[#F97316] font-medium text-center inline-flex items-center justify-center gap-1 w-full">
            <Flame className="w-3 h-3" /> Day 1 — come back tomorrow for a bonus
          </p>
        )}

        {/* CTA if not active today */}
        {!isActiveToday && (
          <p className="text-xs text-[#F97316] font-medium text-center">
            Complete a task to keep your streak!
          </p>
        )}

        {/* Milestone progress */}
        {nextMilestone && (
          <div>
            <div className="flex justify-between text-xs text-[#6B7280] mb-1">
              <span>Next: {nextMilestone.days}-day milestone</span>
              <span>{nextMilestone.remaining} to go</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5">
              <div
                className="bg-amber-500 h-1.5 rounded-full transition-all"
                style={{ width: `${(current / nextMilestone.days) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
